import { Link } from 'react-router-dom'
import { FinalCta } from '../components/FinalCta'
import { Portrait } from '../components/Portrait'
import { Seo } from '../components/Seo'
import { business } from '../config/business'
import { faqs } from '../data/faqs'
import { services } from '../data/services'

const localBusinessSchema = {
  '@context': 'https://schema.org',
  '@type': 'Notary',
  name: business.name,
  url: business.siteUrl,
  image: `${business.siteUrl}/images/michael/profile.jpg`,
  founder: { '@type': 'Person', name: business.owner },
  areaServed: [
    'Bentonville, Arkansas',
    'Rogers, Arkansas',
    'Springdale, Arkansas',
    'Fayetteville, Arkansas',
    'Northwest Arkansas',
  ],
  description: business.description,
  priceRange: '$$–$$$',
}

export function HomePage() {
  return (
    <>
      <Seo
        title="Mobile Notary Services in Northwest Arkansas"
        description="Reliable mobile notary, loan signing, trust signing, I-9 verification, apostille assistance, and document support throughout Northwest Arkansas."
        path="/"
        schema={localBusinessSchema}
      />

      <section className="home-hero">
        <div className="container home-hero-grid">
          <div className="home-hero-copy">
            <span className="eyebrow">Mobile service · Northwest Arkansas</span>
            <h1>Professional notary services, where you need them.</h1>
            <p>Clear, dependable mobile notary and document support from Michael Kidd—brought to your home, office, care facility, or agreed meeting place.</p>
            <div className="hero-actions">
              <Link className="button button-large" to="/request-appointment">Request an appointment</Link>
              <Link className="button button-secondary button-large" to="/pricing">Get a preliminary quote</Link>
            </div>
            <p className="hero-note">Availability and final pricing are always confirmed before scheduling.</p>
          </div>
          <div className="hero-portrait-wrap">
            <Portrait kind="editorial-bw" loading="eager" />
            <div className="portrait-caption"><strong>Michael Kidd</strong><span>Owner · Mobile Notary</span></div>
          </div>
        </div>
      </section>

      <section className="trust-strip" aria-label="Service highlights">
        <div className="container trust-grid">
          <div><strong>Mobile convenience</strong><span>We meet at an agreed location</span></div>
          <div><strong>Northwest Arkansas</strong><span>Clear local travel pricing</span></div>
          <div><strong>Plain-English process</strong><span>Know what to expect before we meet</span></div>
          <div><strong>Professional support</strong><span>Careful, calm, and detail-focused</span></div>
        </div>
      </section>

      <section className="section services-intro">
        <div className="container">
          <div className="section-heading split-heading">
            <div><span className="eyebrow">Services built around real needs</span><h2>From a single signature to a complete signing appointment.</h2></div>
            <p>Choose straightforward notarization, specialized signing support, document coordination, or local field services—all with expectations explained up front.</p>
          </div>
          <div className="service-grid">
            {services.slice(0, 6).map((service, index) => (
              <article className="service-card" key={service.id}>
                <span className="card-number">{String(index + 1).padStart(2, '0')}</span>
                <h3>{service.shortName}</h3>
                <p>{service.summary}</p>
                <Link className="text-link" to={`/services/${service.id}`}>Explore service <span aria-hidden="true">→</span></Link>
              </article>
            ))}
          </div>
          <div className="center-action"><Link className="button button-secondary" to="/services">View all services</Link></div>
        </div>
      </section>

      <section className="section mobile-service-section">
        <div className="container mobile-service-grid">
          <div className="map-card" aria-hidden="true">
            <div className="map-line map-line-one" />
            <div className="map-line map-line-two" />
            <div className="map-pin pin-one"><span /></div>
            <div className="map-pin pin-two"><span /></div>
            <div className="map-pin pin-three"><span /></div>
            <div className="map-label label-one">Bentonville</div>
            <div className="map-label label-two">Rogers</div>
            <div className="map-label label-three">Fayetteville</div>
          </div>
          <div>
            <span className="eyebrow">We come to you</span>
            <h2>A professional appointment without the extra running around.</h2>
            <p>Choose a practical meeting location in Bentonville, Rogers, Springdale, Fayetteville, or elsewhere in Arkansas. Travel pricing is published where it can be calculated clearly; longer routes receive a custom quote.</p>
            <ul className="check-list"><li>Home and office appointments</li><li>Hospitals and care facilities</li><li>Title, escrow, and legal offices</li><li>Agreed public meeting locations</li></ul>
            <Link className="text-link" to="/service-area">See service area and travel fees <span aria-hidden="true">→</span></Link>
          </div>
        </div>
      </section>

      <section className="section pricing-preview">
        <div className="container pricing-preview-grid">
          <div><span className="eyebrow">Pricing without guesswork</span><h2>Clear starting prices. Confirmed before scheduling.</h2><p>Common services use published rates. Complex travel, courier routes, inspection assignments, and apostille handling receive a tailored quote.</p><Link className="button" to="/pricing">View pricing &amp; estimator</Link></div>
          <div className="price-list-card">
            <div><span>Traditional notarization</span><strong>$10</strong><small>per stamp + $10 per signer</small></div>
            <div><span>Loan signings</span><strong>$300</strong><small>flat fee</small></div>
            <div><span>Trust &amp; estate signings</span><strong>$300</strong><small>flat fee</small></div>
            <div><span>I-9 verification support</span><strong>$30</strong><small>plus travel</small></div>
          </div>
        </div>
      </section>

      <section className="section about-preview">
        <div className="container about-preview-grid">
          <Portrait kind="about" />
          <div><span className="eyebrow">A local professional you can reach</span><h2>Meet Michael Kidd.</h2><p>Michael founded Square &amp; Compass Notary to provide clients in Northwest Arkansas with convenient, professional mobile notary and document support services.</p><p>His approach is straightforward: prepare carefully, communicate clearly, arrive ready, and treat every document and appointment with the attention it deserves.</p><Link className="text-link" to="/about">About Michael and his approach <span aria-hidden="true">→</span></Link></div>
        </div>
      </section>

      <section className="section process-section">
        <div className="container"><div className="section-heading centered"><span className="eyebrow">How it works</span><h2>Four clear steps from request to completion.</h2></div><ol className="process-grid"><li><span>1</span><h3>Request</h3><p>Share the service, location, timing, and practical document counts.</p></li><li><span>2</span><h3>Confirm</h3><p>Michael confirms eligibility, availability, travel, and final pricing.</p></li><li><span>3</span><h3>Meet or connect</h3><p>Bring the complete documents, required people, and acceptable identification.</p></li><li><span>4</span><h3>Complete</h3><p>The requested notarial or document-support steps are completed and returned as agreed.</p></li></ol></div>
      </section>

      <section className="section faq-preview">
        <div className="container faq-preview-grid"><div><span className="eyebrow">Good questions are welcome</span><h2>What clients ask before an appointment.</h2><p>Clear preparation makes the appointment smoother. These answers cover the essentials without stepping into legal advice.</p><Link className="button button-secondary" to="/faq">Read all FAQs</Link></div><div className="faq-list">{faqs.slice(0, 4).map((faq) => <details key={faq.question}><summary>{faq.question}</summary><p>{faq.answer}</p></details>)}</div></div>
      </section>

      <FinalCta />
    </>
  )
}
